import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Activity, Cloud, ShieldCheck, Sparkles, Cpu, Folders } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FCFCFC] text-[#0A0A0A]" data-testid="landing-page">
      {/* Top bar */}
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="label-eyebrow text-neutral-500">QUANTZILLION</span>
            <span className="font-display font-black tracking-tighter text-xl">IoT // CONTROL</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="#features"
              className="hidden sm:inline-block px-3 py-2 text-sm hover:bg-neutral-100"
              data-testid="nav-features"
            >Features</a>
            <a
              href="#how"
              className="hidden sm:inline-block px-3 py-2 text-sm hover:bg-neutral-100"
              data-testid="nav-how"
            >How it works</a>
            <Link
              to="/login"
              data-testid="header-signin-btn"
              className="px-4 py-2 text-sm font-semibold border border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-neutral-200">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-[#002FA7] mb-6" data-testid="hero-eyebrow">
              Industrial IoT · Multi-Tenant · AWS IoT Core
            </div>
            <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Thousands of sensors.<br />
              <span className="text-[#002FA7]">One control room.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-neutral-700 leading-relaxed">
              A web platform that lets any WiFi or mobile-connected device stream readings into AWS IoT Core
              under your account &mdash; with anomaly detection and Claude-powered predictions baked in.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/login"
                data-testid="hero-cta-signin"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#002FA7] text-white font-semibold hover:bg-[#001E70] transition-colors"
              >
                Sign in with Google
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <a
                href="#features"
                data-testid="hero-cta-features"
                className="inline-flex items-center gap-2 px-5 py-3 border border-neutral-900 font-semibold hover:bg-neutral-900 hover:text-white transition-colors"
              >
                See features
              </a>
            </div>

            <dl className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
              {[
                ["1000s", "Devices / account"],
                ["MQTT+HTTPS", "Ingest paths"],
                ["Claude 4.5", "Predictive AI"],
              ].map(([k, v]) => (
                <div key={v} className="border-t border-neutral-900 pt-3" data-testid={`stat-${v}`}>
                  <div className="font-mono text-2xl font-bold tracking-tight">{k}</div>
                  <div className="text-xs text-neutral-600 mt-1 uppercase tracking-widest">{v}</div>
                </div>
              ))}
            </dl>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] w-full border border-neutral-900 overflow-hidden bg-neutral-900 relative">
              <img
                src="https://images.pexels.com/photos/5532986/pexels-photo-5532986.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Industrial control panel"
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent" />
              <div className="absolute left-4 right-4 bottom-4 border border-white/40 bg-black/50 backdrop-blur-md p-4">
                <div className="label-eyebrow text-white/70">Live</div>
                <div className="font-mono text-white text-xl mt-1">2,847 devices reporting</div>
                <div className="text-white/70 text-xs mt-1">Last 60s · 0 packet loss</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex border border-neutral-900 bg-white px-4 py-3 items-center gap-3 shadow-[6px_6px_0_0_#002FA7]">
              <Activity className="w-4 h-4 text-[#002FA7]" />
              <span className="font-mono text-sm">anomaly_score = 0.04 ✓ nominal</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-eyebrow text-neutral-500">// FEATURES</div>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3 max-w-2xl">
            Everything an operator needs to run a sensor fleet.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {[
              { icon: Cpu, title: "Device registry", body: "Register 1000s of sensors per account. Per-device API key, optional AWS IoT thing + cert." },
              { icon: Cloud, title: "AWS IoT Core", body: "Provision things, certs and policies from the web app. Direct MQTT publish to your AWS account." },
              { icon: Activity, title: "Live telemetry", body: "Real-time charts, recent readings tables, multi-metric overlays. CSV export for any window." },
              { icon: ShieldCheck, title: "Thresholds & alerts", body: "Per-metric min/max rules, auto-flag offending devices, acknowledge from the dashboard." },
              { icon: Sparkles, title: "Predictive AI", body: "Z-score anomaly flagging on ingest + Claude Sonnet 4.5 daily briefing in plain English." },
              { icon: Folders, title: "Multi-tenant", body: "Every account is fully isolated. Sign in with Google, invite teammates later." },
            ].map((f) => (
              <div key={f.title} className="bg-white p-6 hover:bg-neutral-50 transition-colors" data-testid={`feature-${f.title}`}>
                <f.icon className="w-5 h-5 text-[#002FA7]" strokeWidth={1.75} />
                <div className="mt-4 font-display font-semibold text-lg">{f.title}</div>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-neutral-500">// PIPELINE</div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">
              From sensor to insight in 4 steps.
            </h2>
          </div>
          <ol className="lg:col-span-8 space-y-px bg-neutral-200 border border-neutral-200">
            {[
              ["01", "Register your device", "Create an account, add a device. Receive an API key (and optionally an AWS IoT certificate)."],
              ["02", "Stream readings", "Devices POST to /api/telemetry/ingest or publish MQTT to AWS IoT under your thing name."],
              ["03", "Detect & alert", "Real-time z-score anomaly detection plus your custom min/max thresholds trigger alerts instantly."],
              ["04", "Ask the AI", "Claude Sonnet 4.5 summarises your fleet, predicts risks, and answers ad-hoc questions about the data."],
            ].map(([n, t, d]) => (
              <li key={n} className="bg-white p-6 flex gap-6 items-start" data-testid={`step-${n}`}>
                <div className="font-mono text-2xl text-[#002FA7] font-bold w-16 shrink-0">{n}</div>
                <div>
                  <div className="font-display font-semibold text-lg">{t}</div>
                  <p className="text-sm text-neutral-600 mt-1 leading-relaxed max-w-2xl">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl max-w-xl leading-[0.95]">
              Ready to wire up your first sensor?
            </h2>
            <p className="text-neutral-600 mt-4 max-w-xl">
                  Free to try with the built-in simulator. Plug your AWS IoT Core credentials in whenever you&apos;re ready &mdash; nothing is sent to AWS until you do.
            </p>
          </div>
          <Link
            to="/login"
            data-testid="footer-cta-signin"
            className="inline-flex items-center gap-2 px-6 py-4 bg-[#002FA7] text-white font-semibold text-lg hover:bg-[#001E70]"
          >
            Sign in with Google <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-8 text-center text-xs text-neutral-500 font-mono">
        © Quantzillion IoT // CONTROL
      </footer>
    </div>
  );
}
